import { Grid } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormAutocomplete } from "../form-fields";
import { useFormContext } from "react-hook-form";
import { SelectElementModelBE, SelectElementModelFE } from "../../api/types/generic";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { FormRentalPeriodModel } from "../../api/types/rentalPeriods";
import { FormAncillaryModel } from "../../api/types/ancillary";
import { FormRateModel } from "../../api/types/rate";
import { FormExceptionListAncillaryExceptionModel } from "../../api/types/exceptionListAncillaryExceptionModel";
import { FormUpsellingRateModel } from "../../api/types/upsellingRate";
import { FormLocationGroupLocationModel } from "../../api/types/locationGroupLocation";

export type DealerTenantEntity = {
    id: number;
    name: string;
    legalEntities: SelectElementModelBE[];
};

const DealerAndTenant = ({
                             readOnly,
                             md,
                             legalEntityName,
                             dealerName,
                             idForReadOnly,
                             dealerIsVisible,
                         }: {
    readOnly?: boolean;
    md: number;
    legalEntityName: string;
    dealerName: string;
    idForReadOnly?: number;
    dealerIsVisible?: boolean;
}) => {
    const { t } = useTranslation(["translation", "pricing"]);
    const { watch, setValue, register } = useFormContext<
        | FormRentalPeriodModel
        | FormAncillaryModel
        | FormRateModel
        | FormExceptionListAncillaryExceptionModel
        | FormUpsellingRateModel
        | FormLocationGroupLocationModel
    >();

    const {
        globalData: { user },
    } = useContext(GlobalContext);

    const [dealerOptions, setDealerOptions] = useState<SelectElementModelFE[]>([]);

    const legalEntityOptions: SelectElementModelFE[] = useMemo(() => {
        return user.legalEntities?.map((el: { name: string; }) => ({
            code: el.name,
            label: t("translation:legalEntities.".concat(el.name)),
        })) ?? [];
    }, [user.legalEntities, t]);

    const allDealers = user.dealers ?? [];

    const handleLegalEntityChange = (value: SelectElementModelFE) => {
        // @ts-ignore
        setValue(legalEntityName, value);
        // @ts-ignore
        setValue(dealerName, null);

        const relatedDealers = allDealers
            .filter((dealer:any) =>
                dealer.legalEntities?.some((le: { name: string | number; }) => le.name === value?.code)
            )
            .map((dealer: { name: string; }) => ({
                code: dealer.name,
                label: t("translation:dealers.".concat(dealer.name)),
            }));

        setDealerOptions(relatedDealers);

        if (relatedDealers.length === 1) {
            // @ts-ignore
            setValue(dealerName, relatedDealers[0]);
        }
    };

    useEffect(() => {
        if (legalEntityOptions.length === 1) {
            const singleLE = legalEntityOptions[0];
            // @ts-ignore
            setValue(legalEntityName, singleLE, { shouldDirty: true });

            const relatedDealers = allDealers
                .filter((dealer: { legalEntities: any[]; }) =>
                    dealer.legalEntities?.some((le) => le.name === singleLE?.code)
                )
                .map((dealer: { name: string; }) => ({
                    code: dealer.name,
                    label: t("translation:dealers.".concat(dealer.name)),
                }));

            setDealerOptions(relatedDealers);

            if (relatedDealers.length === 1) {
                // @ts-ignore
                setValue(dealerName, relatedDealers[0], { shouldDirty: true });
            }
        }
    }, [legalEntityOptions, allDealers, setValue, legalEntityName, dealerName, t]);

    const shouldShowLegalEntity = (user.legalEntities?.length ?? 0) > 1;
    const shouldShowDealer =
        (dealerIsVisible ?? true) && (user.dealers?.length ?? 0) > 1;

    return (
        <>
            {shouldShowLegalEntity && (
                <Grid item xs={12} md={md}>
                    <FormAutocomplete
                        // @ts-ignore
                        {...register(legalEntityName)}
                        label={`${t("pricing:dealer.tenant")}*`}
                        options={legalEntityOptions}
                        isOptionEqualToValue={(option, value) => option?.code === value?.code}
                        handleChange={handleLegalEntityChange}
                        readOnly={readOnly || Boolean(idForReadOnly)}
                    />
                </Grid>
            )}
            {shouldShowDealer && (
                <Grid item xs={12} md={md}>
                    <FormAutocomplete
                        // @ts-ignore
                        {...register(dealerName)}
                        label={`${t("pricing:dealer.dealer")}*`}
                        options={dealerOptions}
                        isOptionEqualToValue={(option, value) => option?.code === value?.code}
                        readOnly={readOnly || Boolean(idForReadOnly)}
                    />
                </Grid>
            )}
        </>
    );
};

export default DealerAndTenant;
